import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function AutomationPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activePerspective, setActivePerspective] = useState<'owner' | 'employee' | 'client'>('owner');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const perspectives = {
    owner: {
      title: 'Business Owner',
      icon: 'ri-briefcase-line',
      benefits: [
        { icon: 'ri-money-dollar-circle-line', text: 'Capture every lead and increase monthly revenue' },
        { icon: 'ri-time-line', text: 'Reclaim 10-15 hours per week from admin work' },
        { icon: 'ri-line-chart-line', text: 'Scale operations without hiring additional staff' },
        { icon: 'ri-shield-check-line', text: 'Never miss opportunities due to missed calls' }
      ]
    },
    employee: {
      title: 'Your Team',
      icon: 'ri-team-line',
      benefits: [
        { icon: 'ri-checkbox-circle-line', text: 'Eliminate repetitive scheduling and data entry' },
        { icon: 'ri-focus-3-line', text: 'Focus on high-value work instead of admin tasks' },
        { icon: 'ri-calendar-check-line', text: 'Organized systems that prevent double-booking' },
        { icon: 'ri-emotion-happy-line', text: 'Reduced stress from managing constant inquiries' }
      ]
    },
    client: {
      title: 'Your Customers',
      icon: 'ri-user-heart-line',
      benefits: [
        { icon: 'ri-24-hours-line', text: 'Get answers and book appointments 24/7' },
        { icon: 'ri-flashlight-line', text: 'Instant response—no waiting for callbacks' },
        { icon: 'ri-customer-service-2-line', text: 'Professional, consistent service every time' },
        { icon: 'ri-calendar-event-line', text: 'Easy scheduling that fits their schedule' }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white shadow-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/56592420ad0c806e82c83516867f5679/d1b89c1c9827b7fc761aa972a1497809.png" 
                alt="Smart Site Solutions" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold text-[#1A2332]">Smart Site Solutions</span>
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link 
                to="/"
                className="text-base font-medium text-gray-700 hover:text-[#4A9FF5] transition-colors"
              >
                Home
              </Link>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#4A9FF5] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#2B7FE8] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#4A9FF5] via-[#3DD5D5] to-[#4A9FF5] pt-20">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-10 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 rounded-full bg-white/20 text-white font-medium mb-6 backdrop-blur-sm">
            AI-Powered Business Automation
          </div>
          
          <h1 className="text-6xl md:text-7xl font-bold text-white leading-tight mb-6 animate-fade-in-up">
            Never Miss Another Lead.<br />
            <span className="text-white/90">
              Operate 24/7 Without Extra Staff.
            </span>
          </h1>

          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Automation that handles calls, captures leads, books appointments, and manages follow-ups—saving time and boosting revenue without hiring anyone new.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <button 
              onClick={() => scrollToSection('contact')}
              className="bg-white text-[#4A9FF5] px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-50 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              See How It Works for You
            </button>
            <button 
              onClick={() => scrollToSection('pain-points')}
              className="bg-transparent text-white px-8 py-4 rounded-full text-lg font-semibold border-2 border-white hover:bg-white hover:text-[#4A9FF5] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              View the Problems We Solve
            </button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-white"></i>
        </div>
      </section>

      {/* Pain Points Section */}
      <section id="pain-points" className="py-24 bg-[#F7F9FC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 rounded-lg bg-red-500 text-white font-medium mb-4">
              The Cost of Manual Operations
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              How Much Is Disorganization<br />Costing Your Business?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every missed call, forgotten follow-up, and manual task is money left on the table
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
                <i className="ri-phone-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-3">Missed Calls = Lost Revenue</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                After-hours calls, busy lines, and voicemail tags mean potential customers are calling your competitors instead.
              </p>
              <div className="text-red-500 font-bold text-lg">Average loss: $5,000-$15,000/month</div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
                <i className="ri-time-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-3">Hours Lost to Admin Work</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Scheduling appointments, answering the same questions, and chasing leads eats up billable hours.
              </p>
              <div className="text-red-500 font-bold text-lg">Average loss: 10-15 hours/week</div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
                <i className="ri-team-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-3">Staff Overloaded</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Your team is buried in repetitive tasks instead of focusing on high-value work that grows your business.
              </p>
              <div className="text-red-500 font-bold text-lg">Reduced productivity &amp; morale</div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
                <i className="ri-alert-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-3">Leads Falling Through</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Without organized systems, follow-ups get forgotten, appointments are missed, and qualified leads disappear.
              </p>
              <div className="text-red-500 font-bold text-lg">30-40% lead loss rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Perspectives Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              Automation Benefits Everyone
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our automation systems improve operations from every angle
            </p>
          </div>

          {/* Perspective Tabs */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-[#F7F9FC] rounded-full p-2 gap-2">
              <button
                onClick={() => setActivePerspective('owner')}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activePerspective === 'owner'
                    ? 'bg-[#4A9FF5] text-white shadow-lg'
                    : 'text-gray-600 hover:text-[#4A9FF5]'
                }`}
              >
                <i className="ri-briefcase-line mr-2"></i>
                Business Owner
              </button>
              <button
                onClick={() => setActivePerspective('employee')}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activePerspective === 'employee'
                    ? 'bg-[#4A9FF5] text-white shadow-lg'
                    : 'text-gray-600 hover:text-[#4A9FF5]'
                }`}
              >
                <i className="ri-team-line mr-2"></i>
                Your Team
              </button>
              <button
                onClick={() => setActivePerspective('client')}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activePerspective === 'client'
                    ? 'bg-[#4A9FF5] text-white shadow-lg'
                    : 'text-gray-600 hover:text-[#4A9FF5]'
                }`}
              >
                <i className="ri-user-heart-line mr-2"></i>
                Your Customers
              </button>
            </div>
          </div>

          {/* Perspective Content */}
          <div className="bg-gradient-to-br from-[#F7F9FC] to-white rounded-3xl p-12 shadow-xl">
            <div className="flex items-center justify-center mb-8">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center">
                <i className={`${perspectives[activePerspective].icon} text-4xl text-white`}></i>
              </div>
            </div>
            
            <h3 className="text-4xl font-bold text-[#1A2332] text-center mb-12">
              {perspectives[activePerspective].title} Perspective
            </h3>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {perspectives[activePerspective].benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#4A9FF5]/10 flex items-center justify-center flex-shrink-0">
                      <i className={`${benefit.icon} text-2xl text-[#4A9FF5]`}></i>
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed pt-2">{benefit.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-[#F7F9FC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 rounded-lg bg-[#4A9FF5] text-white font-medium mb-4">
              Simple Setup, Powerful Results
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              How Our Automation Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Minimal setup required. Maximum impact on your business operations.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center text-white text-2xl font-bold mb-6">
                <i className="ri-phone-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-4">24/7 Call Handling</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                AI-powered system answers calls, qualifies leads, and books appointments automatically—even after hours or when you're busy.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Natural conversation flow</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Instant lead qualification</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Direct calendar integration</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center text-white text-2xl font-bold mb-6">
                <i className="ri-calendar-check-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-4">Smart Scheduling</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Automated appointment booking that syncs with your calendar, sends reminders, and eliminates double-booking or no-shows.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Real-time availability</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Automatic reminders</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Rescheduling handled automatically</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center text-white text-2xl font-bold mb-6">
                <i className="ri-mail-send-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-4">Automated Follow-Ups</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Never lose a lead to forgotten follow-ups. Automated sequences nurture prospects and keep your business top-of-mind.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Personalized messaging</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Multi-channel outreach</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Conversion tracking</span>
                </div>
              </div>
            </div>
          </div>

          {/* Setup Process */}
          <div className="bg-white rounded-3xl p-12 shadow-xl">
            <h3 className="text-3xl font-bold text-[#1A2332] text-center mb-12">
              Setup Takes Days, Not Weeks
            </h3>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#4A9FF5] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  1
                </div>
                <h4 className="font-bold text-[#1A2332] mb-2">Discovery Call</h4>
                <p className="text-sm text-gray-600">We learn your business flow and pain points</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#4A9FF5] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  2
                </div>
                <h4 className="font-bold text-[#1A2332] mb-2">Custom Build</h4>
                <p className="text-sm text-gray-600">We configure automation for your specific needs</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#4A9FF5] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  3
                </div>
                <h4 className="font-bold text-[#1A2332] mb-2">Test &amp; Train</h4>
                <p className="text-sm text-gray-600">You review and we refine until perfect</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#4A9FF5] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  4
                </div>
                <h4 className="font-bold text-[#1A2332] mb-2">Go Live</h4>
                <p className="text-sm text-gray-600">Start capturing leads immediately</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 rounded-lg bg-[#4A9FF5]/10 text-[#4A9FF5] font-medium mb-4">
              Real Business Results
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              What You Get With Automation
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] rounded-3xl p-10 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="text-6xl font-bold mb-4">24/7</div>
              <h3 className="text-2xl font-bold mb-3">Capture Every Lead</h3>
              <p className="text-white/90 leading-relaxed">
                Your business never sleeps. Automation handles inquiries, qualifies prospects, and books appointments around the clock—even when you're closed.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] rounded-3xl p-10 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="text-6xl font-bold mb-4">10-15hrs</div>
              <h3 className="text-2xl font-bold mb-3">Reclaim Your Time</h3>
              <p className="text-white/90 leading-relaxed">
                Stop wasting hours on repetitive admin tasks. Automation handles scheduling, follow-ups, and data entry so you can focus on revenue-generating work.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] rounded-3xl p-10 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="text-6xl font-bold mb-4">$0</div>
              <h3 className="text-2xl font-bold mb-3">No Extra Staff Needed</h3>
              <p className="text-white/90 leading-relaxed">
                Scale your operations without hiring receptionists or admin staff. Automation handles the workload consistently and professionally.
              </p>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="bg-[#F7F9FC] rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-[#4A9FF5]/10 flex items-center justify-center">
                  <i className="ri-line-chart-line text-3xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1A2332]">Increase Monthly Revenue</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Convert more website visitors into paying customers. Automated systems capture, qualify, and nurture every lead—turning prospects into revenue.
              </p>
            </div>

            <div className="bg-[#F7F9FC] rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-[#4A9FF5]/10 flex items-center justify-center">
                  <i className="ri-shield-check-line text-3xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1A2332]">Eliminate Human Error</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Automated systems handle every customer interaction consistently and professionally—no forgotten follow-ups, missed appointments, or lost details.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-[#1A2332] via-[#2B3F5C] to-[#1A2332] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#4A9FF5]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#3DD5D5]/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Ready to Automate Your Business?
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            See exactly how automation will work for your business—free consultation, no pressure, fast response
          </p>

          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <form 
              id="automation_contact_form"
              data-readdy-form
              action="https://readdy.ai/api/form/d58skd93kamldd15634g"
              method="POST"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                
                fetch('https://readdy.ai/api/form/d58skd93kamldd15634g', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: new URLSearchParams(formData as any).toString(),
                })
                .then(response => {
                  if (response.ok) {
                    alert('Thank you! We\'ll be in touch within 24 hours to discuss your automation needs.');
                    form.reset();
                  } else {
                    alert('Something went wrong. Please try again.');
                  }
                })
                .catch(() => {
                  alert('Something went wrong. Please try again.');
                });
              }}
              className="space-y-5"
            >
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Your Phone Number"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="text"
                name="business_name"
                placeholder="Business Name"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="text"
                name="business_type"
                placeholder="Type of Business (e.g., HVAC, Plumbing, Consulting)"
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <button
                type="submit"
                className="w-full bg-[#4A9FF5] text-white py-4 rounded-xl text-lg font-semibold hover:bg-[#2B7FE8] transition-all duration-300 hover:scale-[1.02] shadow-lg whitespace-nowrap cursor-pointer"
              >
                Get Your Free Automation Consultation
              </button>
            </form>

            <div className="flex items-center justify-center space-x-6 mt-8 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-[#4A9FF5]"></i>
                <span>Response within 24 hours</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-[#4A9FF5]"></i>
                <span>No obligation</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-[#4A9FF5]"></i>
                <span>Custom solution</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A2332] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/56592420ad0c806e82c83516867f5679/d1b89c1c9827b7fc761aa972a1497809.png" 
                  alt="Smart Site Solutions" 
                  className="h-8 w-auto"
                />
                <span className="text-xl font-bold text-white">Smart Site Solutions</span>
              </Link>
              <p className="text-gray-400">Websites and automation built for service businesses</p>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Contact Us</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>613-899-4468</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>613-621-2422</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>613-203-2762</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-mail-line"></i>
                  <span>Smartsitesolutions1@gmail.com</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Quick Links</h4>
              <div className="space-y-3">
                <Link 
                  to="/"
                  className="block text-gray-400 hover:text-[#4A9FF5] transition-colors"
                >
                  Home
                </Link>
                <button 
                  onClick={() => scrollToSection('pain-points')}
                  className="block text-gray-400 hover:text-[#4A9FF5] transition-colors cursor-pointer text-left"
                >
                  Problems We Solve
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="block text-gray-400 hover:text-[#4A9FF5] transition-colors cursor-pointer text-left"
                >
                  Contact
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Get Started</h4>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#4A9FF5] text-white px-6 py-3 rounded-full font-medium hover:bg-[#2B7FE8] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Schedule Consultation
              </button>
            </div>
          </div>

          <div className="pt-8 border-t-2 border-[#4A9FF5]/20 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">© 2025 Smart Site Solutions. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 text-sm hover:text-[#4A9FF5] transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
